"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useCallback, type ChangeEvent, type SubmitEvent } from "react";
import { Send } from "lucide-react";
import { useWebSocket } from "../../lib/useWebSocket";
import { getRoomMessages, getRooms, createRoom, type Room } from "../../lib/api";
import { useSession } from "@/lib/auth-client";
import { Input, Button } from "@/components/ui";
import { Textarea } from "@chatapp/ui/components/textarea";
interface ChatMessage {
  _id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
  read: boolean;
}
export default function ChatPage() {
  const $ = _c(96);
  const {
    data: session,
    isPending
  } = useSession();
  const userId = session?.user.id || null;
  const senderName = session?.user.name || "Unknown";
  const {
    messages,
    setMessages,
    typingUser,
    isConnected,
    joinRoom,
    sendMessage,
    sendTyping
  } = useWebSocket(userId);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [rooms, setRooms] = useState(t0);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [input, setInput] = useState("");
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomDescription, setNewRoomDescription] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  let t1;
  let t2;
  if ($[1] !== activeRoomId || $[2] !== userId) {
    t1 = () => {
      if (!userId) {
        return;
      }
      setIsLoading(true);
      getRooms().then(fetchedRooms => {
        setRooms(fetchedRooms);
        if (!activeRoomId && fetchedRooms.length > 0) {
          setActiveRoomId(fetchedRooms[0]._id);
        }
      }).catch(err => setError(err instanceof Error ? err.message : "Unable to load rooms.")).finally(() => setIsLoading(false));
    };
    t2 = [userId, activeRoomId];
    $[1] = activeRoomId;
    $[2] = userId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== activeRoomId || $[6] !== joinRoom || $[7] !== setMessages) {
    t3 = () => {
      if (!activeRoomId) {
        return;
      }
      setIsLoading(true);
      getRoomMessages(activeRoomId).then(roomMessages => setMessages(roomMessages)).catch(err_0 => setError(err_0 instanceof Error ? err_0.message : "Unable to load messages.")).finally(() => setIsLoading(false));
      joinRoom(activeRoomId);
    };
    t4 = [activeRoomId, joinRoom, setMessages];
    $[5] = activeRoomId;
    $[6] = joinRoom;
    $[7] = setMessages;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== activeRoomId || $[11] !== rooms) {
    t5 = () => {
      setActiveRoom(rooms.find(room => room._id === activeRoomId) ?? null);
    };
    t6 = [activeRoomId, rooms];
    $[10] = activeRoomId;
    $[11] = rooms;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== messages) {
    t8 = [messages];
    $[15] = messages;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = roomId => {
      setActiveRoomId(roomId);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleRoomSelect = t9;
  let t10;
  if ($[18] !== newRoomDescription || $[19] !== newRoomName) {
    t10 = async event => {
      event.preventDefault();
      if (!newRoomName.trim()) {
        return;
      }
      ;
      try {
        const room_0 = await createRoom(newRoomName.trim(), newRoomDescription.trim());
        setRooms(current => [room_0, ...current]);
        setActiveRoomId(room_0._id);
        setNewRoomName("");
        setNewRoomDescription("");
        setError(null);
      } catch (t11) {
        const err_1 = t11;
        setError(err_1 instanceof Error ? err_1.message : "Failed to create room.");
      }
    };
    $[18] = newRoomDescription;
    $[19] = newRoomName;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleCreateRoom = t10;
  useRef(null);
  let t11;
  if ($[21] !== activeRoomId || $[22] !== sendTyping || $[23] !== senderName) {
    t11 = () => {
      if (!activeRoomId) {
        return;
      }
      sendTyping(activeRoomId, senderName);
    };
    $[21] = activeRoomId;
    $[22] = sendTyping;
    $[23] = senderName;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleTyping = t11;
  let t12;
  if ($[25] !== handleTyping) {
    t12 = event_0 => {
      setInput(event_0.target.value);
      handleTyping();
    };
    $[25] = handleTyping;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleInputChange = t12;
  let t13;
  if ($[27] !== activeRoomId || $[28] !== input || $[29] !== sendMessage || $[30] !== senderName) {
    t13 = event_1 => {
      event_1.preventDefault();
      if (!activeRoomId || !input.trim()) {
        return;
      }
      sendMessage(activeRoomId, senderName, input.trim());
      setInput("");
    };
    $[27] = activeRoomId;
    $[28] = input;
    $[29] = sendMessage;
    $[30] = senderName;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const handleSubmit = t13;
  if (isPending) {
    let t14;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="p-6">Loading session...</div>;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    return t14;
  }
  if (!userId) {
    let t14;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="p-6 text-center"><div className="text-lg font-semibold">Sign in to join chat rooms.</div><p className="mt-2 text-sm text-muted-foreground">Use the login page to authenticate before chatting.</p></div>;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    return t14;
  }
  let t14;
  if ($[34] !== activeRoomId || $[35] !== handleCreateRoom || $[36] !== isConnected || $[37] !== isLoading || $[38] !== isSidebarOpen || $[39] !== newRoomDescription || $[40] !== newRoomName || $[41] !== rooms) {
    t14 = isSidebarOpen ? <aside className="flex-shrink-0 w-[240px] flex flex-col gap-4 rounded-lg border border-border p-4 bg-panel"><div><div className="width-full"><h2 className="text-lg font-semibold float-left text-left">Rooms</h2><span className={`text-xs font-medium ${isConnected ? "text-emerald-600" : "text-rose-600"} float-right text-right mt-2`}>{isConnected ? "Connected" : "Offline"}</span><div className="clear-both" /><p className="text-sm text-muted-foreground width-full mt-4 mb-6">Join a room to chat live.</p></div><div className="space-y-2 overflow-y-auto">{isLoading && <div className="text-sm text-muted-foreground">Loading rooms…</div>}{!isLoading && rooms.length === 0 && <div className="text-sm text-muted-foreground">No rooms yet. Create one.</div>}{rooms.map(room_1 => <button key={room_1._id} type="button" onClick={() => handleRoomSelect(room_1._id)} className={`block w-full text-left rounded-md border px-3 py-2 ${room_1._id === activeRoomId ? "border-primary bg-primary/10" : "border-border bg-background"}`}><div className="font-semibold">{room_1.name}</div><div className="text-sm text-muted-foreground">{room_1.description ?? "No description"}</div></button>)}</div><form onSubmit={handleCreateRoom} className="space-y-3 pt-4 border-t border-muted/20"><div><label htmlFor="room-name" className="block text-sm font-medium mb-1">New room</label><Input id="room-name" value={newRoomName} onChange={event_2 => setNewRoomName(event_2.target.value)} placeholder="Room name" className="w-full" /></div><div><Input id="room-description" value={newRoomDescription} onChange={event_3 => setNewRoomDescription(event_3.target.value)} placeholder="Description (optional)" className="w-full" /></div><Button type="submit" className="w-full font-extrabold">Create Room</Button></form></div></aside> : null;
    $[34] = activeRoomId;
    $[35] = handleCreateRoom;
    $[36] = isConnected;
    $[37] = isLoading;
    $[38] = isSidebarOpen;
    $[39] = newRoomDescription;
    $[40] = newRoomName;
    $[41] = rooms;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const t15 = activeRoom?.name ?? "Select a room";
  let t16;
  if ($[43] !== t15) {
    t16 = <h2 className="text-xl font-semibold">{t15}</h2>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = activeRoom?.description ?? "Pick a room from the list to see messages.";
  let t18;
  if ($[45] !== t17) {
    t18 = <p className="text-sm text-muted-foreground">{t17}</p>;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t18) {
    t19 = <div>{t16}{t18}</div>;
    $[47] = t16;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== messages.length) {
    t20 = <div className="text-sm text-muted-foreground">{messages.length} messages</div>;
    $[50] = messages.length;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsSidebarOpen(_temp);
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  const t22 = isSidebarOpen ? "Hide rooms" : "Show rooms";
  let t23;
  if ($[53] !== t22) {
    t23 = <Button type="button" variant="secondary" className="h-9 text-sm" onClick={t21}>{t22}</Button>;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t20 || $[56] !== t23) {
    t24 = <div className="flex flex-wrap items-center gap-3">{t20}{t23}</div>;
    $[55] = t20;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t19 || $[59] !== t24) {
    t25 = <div className="mb-4 flex items-center justify-between gap-4">{t19}{t24}</div>;
    $[58] = t19;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== error) {
    t26 = error && <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>;
    $[61] = error;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== messages || $[64] !== senderName || $[65] !== userId) {
    t27 = messages.length === 0 ? <div className="text-center text-muted-foreground py-20">No messages yet in this room.</div> : messages.map(message => <div key={message._id} className={`rounded-2xl px-4 py-3 w-3/5 text-white-800 ${message.senderId === userId ? "bg-slate-600 ml-auto text-right " : "bg-gray-800 mr-auto text-left others"}`}><div className="text-xs text-gray-400 font-semibold mb-1">{message.senderId === userId ? senderName : message.senderName}</div><div>{message.content}</div><div className="mt-1 text-[11px] text-muted-foreground">{new Date(message.createdAt).toLocaleTimeString()}</div></div>);
    $[63] = messages;
    $[64] = senderName;
    $[65] = userId;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div ref={messagesEndRef} />;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t26 || $[69] !== t27) {
    t29 = <div className="flex-1 overflow-y-auto space-y-3 rounded-lg border border-background p-3">{t26}{t27}{t28}</div>;
    $[68] = t26;
    $[69] = t27;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  const t30 = typingUser ? `${typingUser} is typing...` : " ";
  let t31;
  if ($[71] !== t30) {
    t31 = <div className="mt-2 min-h-[1.5rem] text-sm text-muted-foreground">{t30}</div>;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  const t32 = activeRoom ? "Type a message..." : "Select a room first";
  const t33 = !activeRoom;
  let t34;
  if ($[73] !== handleInputChange || $[74] !== input || $[75] !== t32 || $[76] !== t33) {
    t34 = <Input value={input} onChange={handleInputChange} placeholder={t32} className="flex-1" disabled={t33} autoComplete="off" />;
    $[73] = handleInputChange;
    $[74] = input;
    $[75] = t32;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== activeRoom || $[79] !== input) {
    t35 = !activeRoom || input.trim().length === 0;
    $[78] = activeRoom;
    $[79] = input;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Send size={18} />;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t35) {
    t37 = <Button type="submit" disabled={t35}>{t36}</Button>;
    $[82] = t35;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== handleSubmit || $[85] !== t34 || $[86] !== t37) {
    t38 = <form onSubmit={handleSubmit} className="mt-4 flex items-center gap-2">{t34}{t37}</form>;
    $[84] = handleSubmit;
    $[85] = t34;
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== t25 || $[89] !== t29 || $[90] !== t31 || $[91] !== t38) {
    t39 = <section className="basis-[80%] max-w-[80%] min-w-0 flex flex-col rounded-lg border border-border bg-panel p-4">{t25}{t29}{t31}{t38}</section>;
    $[88] = t25;
    $[89] = t29;
    $[90] = t31;
    $[91] = t38;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== t14 || $[94] !== t39) {
    t40 = <div className="flex w-full min-w-0 justify-center gap-4 p-4 max-w-8xl mx-auto h-[calc(100svh-4rem)]">{t14}{t39}</div>;
    $[93] = t14;
    $[94] = t39;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  return t40;
}
function _temp(open) {
  return !open;
}